import { AnalyticsProvider, MasterPage as MasterPageShared, useOidcAuthentication } from '@cp-shared-19/frontend-ui';
import { IconFooter } from 'components/footer/icon-footer';
import { LegalFooter } from 'components/footer/legal-footer';
import { NavigationBar } from 'components/navigation/NavigationBar';
import React, { memo, useEffect } from 'react';
import { contactDetailsCheckPagePath, registrationPagePath } from '../../components/navigation/paths';
import { currentBrand } from 'config/config';
import { useMyProfile } from 'components/my-profile';
import { ChatbotWrapper } from 'components/chatbot';
import { useHistory, useLocation } from 'react-router-dom';
import { getErrorPagePathWithErrorType } from 'auth';
import { TimeOutNotification } from 'components/time-out-notification/TimeOutNotification';

export function isIe() {
    const ua = window.navigator.userAgent;
    return ua.includes('MSIE ') || !!ua.match(/Trident.*rv:11\./);
}

const shouldDisplayChatbot = (isAuthenticated: boolean): boolean => {
    return isAuthenticated && !isIe();
};

// eslint-disable-next-line
export type MasterPageProps = {};

export const MasterPage: React.FC<MasterPageProps> = ({ children }) => {
    const { isAuthenticated, urlError } = useOidcAuthentication();
    const location = useLocation();
    const pagesWithoutTimeOutNotification: string[] = [];
    const shouldDisplayTimeOutNotification = (pathname: string) =>
        !pagesWithoutTimeOutNotification.find((pagePath) => pagePath === pathname || `${pagePath}/` === pathname);

    return (
        <>
            {shouldDisplayTimeOutNotification(location.pathname) && <TimeOutNotification />}
            <MasterPageComponent isAuthenticated={isAuthenticated} urlError={urlError as string}>
                {children}
            </MasterPageComponent>
        </>
    );
};

const MasterPageComponent: React.FC<{ isAuthenticated: boolean; urlError: string }> = memo(
    ({ isAuthenticated, urlError, children }) => {
        const { data } = useMyProfile(!isAuthenticated);
        const customerType = data?.identification?.customerType;
        const userGroup = isAuthenticated && !!customerType ? customerType : undefined;
        const location = useLocation();
        const history = useHistory();
        const isContactDetailsCheckPage = location.pathname === contactDetailsCheckPagePath();

        useEffect(() => {
            if (urlError) {
                history.push(getErrorPagePathWithErrorType(urlError));
            }
        }, [urlError]);

        return (
            <AnalyticsProvider
                market={'fr'}
                isAuthenticated={isAuthenticated}
                version={'1'}
                releaseDate={'2020-12-01'}
                language={'fr'}
                userGroup={userGroup}
                brand={currentBrand}
                registrationPath={registrationPagePath()}
                loginPath={'/authentication/callback'}
            >
                <MasterPageShared
                    navigationBar={<NavigationBar />}
                    iconFooter={isContactDetailsCheckPage ? null : <IconFooter />}
                    legalFooter={<LegalFooter />}
                    mainRole="main"
                >
                    <div id="maincontent" />
                    {children}
                    {shouldDisplayChatbot(isAuthenticated) && <ChatbotWrapper />}
                </MasterPageShared>
            </AnalyticsProvider>
        );
    },
);

MasterPageComponent.displayName = 'MasterPageComponent';
